import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { UrlPath } from './UrlPath';

export interface TokenSet {
	idToken: string;
	accessToken: string;
	expiresAt: number; // epoch ms
}

const REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // refresh 5 min before expiry

@Injectable()
export class TokenService {
	private _tokens: TokenSet | null = null;
	private _refreshPromise: Promise<TokenSet> | null = null;

	constructor(
		private http: HttpClient,
		private urlPath: UrlPath,
	) {}

	setTokens(tokens: TokenSet): void {
		this._tokens = tokens;
	}

	getTokens(): TokenSet | null {
		return this._tokens;
	}

	clearTokens(): void {
		this._tokens = null;
	}

	isExpiringSoon(): boolean {
		if (!this._tokens) return true;
		return Date.now() >= this._tokens.expiresAt - REFRESH_THRESHOLD_MS;
	}

	/** Refresh tokens from the httpOnly cookie. Serializes concurrent calls. */
	refresh(): Promise<TokenSet> {
		if (this._refreshPromise) return this._refreshPromise;

		const path = this.urlPath.getAuthPath('refresh');
		this._refreshPromise = firstValueFrom(
			this.http.post<{ idToken: string; accessToken: string; expiresAt: number }>(
				path, {}, { withCredentials: true }
			)
		).then((response) => {
			const tokens: TokenSet = {
				idToken: response.idToken,
				accessToken: response.accessToken,
				expiresAt: response.expiresAt,
			};
			this._tokens = tokens;
			return tokens;
		}).finally(() => {
			this._refreshPromise = null;
		});

		return this._refreshPromise;
	}
}
